(() => {
  if (customElements.get('krbobo-product-menu')) return;

  class ProductMenu extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.timers = new Set();
      const { signal } = this.controller;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.desktop = matchMedia('(min-width: 981px)');
      this.items = [...this.querySelectorAll('.nav-item')].filter(item => item.querySelector('.kr-menu__dropdown'));
      this.resizeObserver = new ResizeObserver(() => this.updateArrows());

      this.items.forEach(item => {
        const trigger = item.querySelector('.nav-btn');
        const dropdown = item.querySelector('.kr-menu__dropdown');
        item.classList.remove('open');
        trigger.setAttribute('aria-expanded', 'false');
        dropdown.hidden = true;
        const tabs = [...item.querySelectorAll('[role="tab"]')].sort((a, b) => Number(a.dataset.categoryOrder) - Number(b.dataset.categoryOrder));
        const panels = [...item.querySelectorAll('[role="tabpanel"]')];
        tabs.forEach((tab, index) => {
          dropdown.querySelector('.kr-menu__categories').append(tab);
          const panel = panels.find(panel => panel.id === tab.getAttribute('aria-controls'));
          dropdown.querySelector('.kr-menu__stage').append(panel);
          tab.setAttribute('aria-selected', String(index === 0));
          tab.tabIndex = index === 0 ? 0 : -1;
          panel.hidden = index !== 0;
          panel.inert = index !== 0;
        });
        let closeTimer;
        const cancelClose = () => {
          clearTimeout(closeTimer);
          this.timers.delete(closeTimer);
        };
        item.addEventListener('pointerenter', event => {
          if (event.pointerType !== 'mouse') return;
          cancelClose();
          this.open(item);
        }, { signal });
        item.addEventListener('pointerleave', event => {
          if (event.pointerType !== 'mouse') return;
          cancelClose();
          closeTimer = setTimeout(() => {
            this.timers.delete(closeTimer);
            if (!item.contains(document.activeElement)) this.close(item);
          }, 180);
          this.timers.add(closeTimer);
        }, { signal });
        item.addEventListener('focusin', () => { cancelClose(); }, { signal });
        item.addEventListener('focusout', event => {
          if (!item.contains(event.relatedTarget) && !item.matches(':hover')) this.close(item);
        }, { signal });
        trigger.addEventListener('click', event => {
          event.preventDefault();
          if (event.detail > 0 && matchMedia('(hover: hover)').matches) this.open(item);
          else if (dropdown.hidden) this.open(item);
          else this.close(item);
        }, { signal });
        trigger.addEventListener('keydown', event => {
          if (event.key !== 'ArrowDown') return;
          event.preventDefault();
          this.open(item);
          tabs.find(tab => tab.getAttribute('aria-selected') === 'true')?.focus();
        }, { signal });
        tabs.forEach((tab, index) => {
          tab.addEventListener('pointerenter', event => {
            if (event.pointerType !== 'mouse') return;
            tab.scrollIntoView({
              block: 'nearest',
              inline: 'nearest',
              behavior: this.reducedMotion.matches ? 'instant' : 'smooth'
            });
            this.select(item, index);
          }, { signal });
          tab.addEventListener('focus', () => this.select(item, index), { signal });
          tab.addEventListener('click', () => this.select(item, index), { signal });
          tab.addEventListener('keydown', event => {
            let next;
            if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
            if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = tabs.length - 1;
            if (next === undefined) return;
            event.preventDefault();
            tabs[next].focus();
            tabs[next].scrollIntoView({ block: 'nearest', inline: 'nearest' });
          }, { signal });
        });
        item.querySelectorAll('.kr-menu__products').forEach(track => {
          track.addEventListener('scroll', () => this.updateTrack(track), { passive: true, signal });
          this.resizeObserver.observe(track);
        });
        item.querySelectorAll('[data-step]').forEach(button => {
          button.addEventListener('click', () => {
            const track = button.parentElement.querySelector('.kr-menu__products');
            const card = track.querySelector('.kr-menu__card');
            if (!card) return;
            const stride = card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap);
            const count = Math.max(1, Math.floor((track.clientWidth + 1) / stride));
            track.scrollBy({ left: Number(button.dataset.step) * stride * count, behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
          }, { signal });
        });
      });
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.items.forEach(item => this.close(item));
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const item = event.target.closest('.nav-item');
        if (!item?.classList.contains('open')) return;
        event.preventDefault();
        this.close(item);
        item.querySelector('.nav-btn').focus();
      }, { signal });
      this.desktop.addEventListener('change', () => {
        if (!this.desktop.matches) this.items.forEach(item => this.close(item));
      }, { signal });
    }

    open(item) {
      if (!this.desktop.matches) return;
      this.items.filter(other => other !== item).forEach(other => this.close(other));
      item.classList.add('open');
      item.querySelector('.nav-btn').setAttribute('aria-expanded', 'true');
      item.querySelector('.kr-menu__dropdown').hidden = false;
      document.body.classList.add('has-open-mega');
      this.updateArrows();
    }

    close(item) {
      item.classList.remove('open');
      item.querySelector('.nav-btn').setAttribute('aria-expanded', 'false');
      item.querySelector('.kr-menu__dropdown').hidden = true;
      document.body.classList.toggle('has-open-mega', !!document.querySelector('krbobo-product-menu .nav-item.open'));
    }

    select(item, index) {
      const tabs = [...item.querySelectorAll('[role="tab"]')];
      const panels = [...item.querySelectorAll('[role="tabpanel"]')];
      const previous = tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true');
      if (index === previous) return;
      const next = panels[index];
      const old = panels[previous];
      // Cancel interrupted transitions before starting the next hover selection.
      panels.forEach(panel => {
        panel.getAnimations().forEach(animation => animation.cancel());
        panel.hidden = panel !== next;
        panel.inert = panel !== next;
        panel.style.zIndex = panel === next ? '1' : '0';
      });
      tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      next.querySelector('.kr-menu__products').scrollLeft = 0;
      if (!this.reducedMotion.matches && old) {
        const direction = index > previous ? 1 : -1;
        const options = { duration: 360, easing: 'cubic-bezier(.22,.61,.36,1)' };
        old.hidden = false;
        const outgoing = old.animate([
          { transform: 'translateX(0)', opacity: 1 },
          { transform: `translateX(${-direction * 100}%)`, opacity: 0 }
        ], options);
        next.animate([
          { transform: `translateX(${direction * 100}%)`, opacity: 0 },
          { transform: 'translateX(0)', opacity: 1 }
        ], options);
        outgoing.finished.then(() => { old.hidden = true; }).catch(() => {});
      }
      this.updateArrows();
    }

    updateTrack(track) {
      if (!track.clientWidth) return;
      const max = track.scrollWidth - track.clientWidth;
      track.parentElement.querySelector('[data-step="-1"]').disabled = track.scrollLeft <= 2;
      track.parentElement.querySelector('[data-step="1"]').disabled = track.scrollLeft >= max - 2;
    }

    updateArrows() {
      this.querySelectorAll('.kr-menu__products').forEach(track => this.updateTrack(track));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.timers?.forEach(timer => clearTimeout(timer));
      this.querySelectorAll('.kr-menu__panel').forEach(panel => panel.getAnimations().forEach(animation => animation.cancel()));
      document.body.classList.toggle('has-open-mega', !!document.querySelector('krbobo-product-menu .nav-item.open'));
    }
  }
  customElements.define('krbobo-product-menu', ProductMenu);
})();
